import React, { useCallback, useRef, useEffect } from 'react';
import { useTranslation } from '@hooks/useTranslation';

const NumberInputMode = ({
  id,
  name,
  value,
  onChange,
  min,
  max,
  unit,
  disabled,
  required,
  values,
  onIncrement,
  onDecrement,
  ...props
}) => {
  const { t } = useTranslation();
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleInputChange = useCallback((e) => {
    const newValue = parseInt(e.target.value) || min;
    onChange(newValue);
  }, [onChange, min]);

  // Répétition automatique pour les boutons +/-
  const startRepeat = useCallback((action) => {
    // Arrêter toute répétition en cours
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Action immédiate
    action();

    // Répétition continue après un délai
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(action, 150);
    }, 300);
  }, []);

  const stopRepeat = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Gestionnaires unifiés pour tous les événements
  const handleStart = useCallback((action) => {
    startRepeat(action);
  }, [startRepeat]);

  const handleEnd = useCallback(() => {
    stopRepeat();
  }, [stopRepeat]);

  // Nettoyer les timers si le composant se démonte
  useEffect(() => {
    return () => stopRepeat();
  }, [stopRepeat]);

  return (
    <div className="integer-input__field-container">
      <button
        type="button"
        className="integer-input__increment integer-input__button"
        onPointerDown={() => handleStart(onIncrement)}
        onPointerUp={handleEnd}
        onPointerLeave={handleEnd}
        onPointerCancel={handleEnd}
        disabled={disabled || values.indexOf(value) === values.length - 1}
        title={t('common.increment')}
      >
        +
      </button>
      <div className="integer-input__input-wrapper">
        <input
          type="number"
          id={id}
          name={name}
          value={value}
          onChange={handleInputChange}
          min={min}
          max={max}
          disabled={disabled}
          className="integer-input__field"
          required={required}
          {...props}
        />
        {unit && (
          <span className="integer-input__unit">{unit}</span>
        )}
      </div>
      <button
        type="button"
        className="integer-input__decrement integer-input__button"
        onPointerDown={() => handleStart(onDecrement)}
        onPointerUp={handleEnd}
        onPointerLeave={handleEnd}
        onPointerCancel={handleEnd}
        disabled={disabled || values.indexOf(value) === 0}
        title={t('common.decrement')}
      >
        −
      </button>
    </div>
  );
};

export default NumberInputMode;
